import React, { useState, useEffect } from 'react';
import { 
    startSecurityScan,
    getVulnerabilities,
    getSecurityDashboard,
    getScanHistory,
    markVulnerabilityFixed,
    markFalsePositive,
    getAIFixSuggestion,
    exportVulnerabilities
} from '../services/api';

// Prevention Templates
const IDOR_FIX_TEMPLATES = {
    'python_django': `# Add ownership check
@login_required
def view_resource(request, resource_id):
    resource = get_object_or_404(Resource, id=resource_id)
    
    # Check ownership
    if resource.owner != request.user:
        raise PermissionDenied("You don't have permission to access this resource")
    
    # Or use Django's permission system
    if not request.user.has_perm('app.view_resource', resource):
        raise PermissionDenied()
    
    return render(request, 'resource.html', {'resource': resource})`,
    
    'python_flask': `# Add authorization decorator
from functools import wraps

def check_resource_access(f):
    @wraps(f)
    def decorated_function(*args, **kwargs):
        resource_id = kwargs.get('resource_id')
        resource = Resource.query.get_or_404(resource_id)
        
        if resource.user_id != current_user.id:
            abort(403, "Access denied")
            
        return f(*args, **kwargs)
    return decorated_function

@app.route('/resource/<int:resource_id>')
@login_required
@check_resource_access
def view_resource(resource_id):
    # Safe to access resource here
    pass`,
    
    'php_laravel': `// Use Laravel policies
class ResourceController extends Controller
{
    public function show(Request $request, $id)
    {
        $resource = Resource::findOrFail($id);
        
        // Authorization check
        $this->authorize('view', $resource);
        
        // Or manual check
        if ($resource->user_id !== Auth::id()) {
            abort(403, 'Unauthorized');
        }
        
        return view('resource.show', compact('resource'));
    }
}`
};

const RACE_CONDITION_FIX_TEMPLATES = {
    'database_transaction': `# Use database transactions with proper locking
from django.db import transaction

@transaction.atomic
def transfer_funds(from_account_id, to_account_id, amount):
    # Lock the accounts to prevent race conditions
    from_account = Account.objects.select_for_update().get(id=from_account_id)
    to_account = Account.objects.select_for_update().get(id=to_account_id)
    
    if from_account.balance < amount:
        raise InsufficientFunds()
    
    from_account.balance -= amount
    to_account.balance += amount
    
    from_account.save()
    to_account.save()`,
    
    'redis_lock': `# Use Redis for distributed locking
import redis
import time

r = redis.Redis()

def with_lock(key, timeout=10):
    identifier = str(uuid.uuid4())
    end = time.time() + timeout
    
    while time.time() < end:
        if r.set(key, identifier, nx=True, ex=timeout):
            try:
                yield
            finally:
                # Only delete if we own the lock
                pipe = r.pipeline(True)
                while True:
                    try:
                        pipe.watch(key)
                        if pipe.get(key) == identifier:
                            pipe.multi()
                            pipe.delete(key)
                            pipe.execute()
                        pipe.unwatch()
                        break
                    except redis.WatchError:
                        pass
            return
        time.sleep(0.001)
    
    raise LockTimeout()`
};

// IDORAndRaceView Component
const IDORAndRaceView = ({ vulnerabilities, onSelectVulnerability }) => {
    const idorVulns = vulnerabilities.filter(v => v.type.includes('IDOR'));
    const raceVulns = vulnerabilities.filter(v => v.type.includes('RACE'));
    
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* IDOR Section */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="text-2xl mr-2">🔓</span>
                    IDOR Vulnerabilities ({idorVulns.length})
                </h3>
                
                {idorVulns.length > 0 ? (
                    <div className="space-y-3">
                        {idorVulns.slice(0, 5).map((vuln, idx) => (
                            <div 
                                key={vuln.id} 
                                className="p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100"
                                onClick={() => onSelectVulnerability(vuln)}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-medium text-red-900">{vuln.title}</p>
                                        <p className="text-sm text-red-700 mt-1">{vuln.file}:{vuln.line}</p>
                                        <span className="inline-block mt-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                            {vuln.type.replace('IDOR_', '').replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                    <span className="text-sm font-medium text-red-600">{vuln.severity}</span>
                                </div>
                            </div>
                        ))}
                        {idorVulns.length > 5 && (
                            <p className="text-sm text-gray-500 text-center">
                                And {idorVulns.length - 5} more...
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <svg className="h-12 w-12 mx-auto mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p>No IDOR vulnerabilities found!</p>
                    </div>
                )}
            </div>
            
            {/* Race Condition Section */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="text-2xl mr-2">🏃</span>
                    Race Conditions ({raceVulns.length})
                </h3>
                
                {raceVulns.length > 0 ? (
                    <div className="space-y-3">
                        {raceVulns.slice(0, 5).map((vuln, idx) => (
                            <div 
                                key={vuln.id} 
                                className="p-3 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100"
                                onClick={() => onSelectVulnerability(vuln)}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-medium text-orange-900">{vuln.title}</p>
                                        <p className="text-sm text-orange-700 mt-1">{vuln.file}:{vuln.line}</p>
                                        <span className="inline-block mt-2 px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">
                                            {vuln.type.replace('RACE_CONDITION_', '').replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                    <span className="text-sm font-medium text-orange-600">{vuln.severity}</span>
                                </div>
                            </div>
                        ))}
                        {raceVulns.length > 5 && (
                            <p className="text-sm text-gray-500 text-center">
                                And {raceVulns.length - 5} more...
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <svg className="h-12 w-12 mx-auto mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p>No race conditions found!</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Prevention Templates Component
const PreventionTemplates = ({ vulnerabilityType, framework }) => {
    const [showTemplate, setShowTemplate] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    
    const getTemplate = () => {
        if (vulnerabilityType?.includes('IDOR')) {
            return IDOR_FIX_TEMPLATES[framework] || IDOR_FIX_TEMPLATES['python_django'];
        } else if (vulnerabilityType?.includes('RACE')) {
            return RACE_CONDITION_FIX_TEMPLATES[framework] || RACE_CONDITION_FIX_TEMPLATES['database_transaction'];
        }
        return null;
    };
    
    const template = getTemplate();
    
    if (!template) return null;
    
    const handleCopy = () => {
        navigator.clipboard.writeText(template);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };
    
    return (
        <div className="mt-4 border-t pt-4">
            <button
                onClick={() => setShowTemplate(!showTemplate)}
                className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
            >
                <svg className={`h-4 w-4 mr-1 transform transition-transform ${showTemplate ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                View Prevention Template
            </button>
            
            {showTemplate && (
                <div className="mt-3 bg-gray-900 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
                        <span className="text-sm text-gray-300">Prevention Template</span>
                        <button
                            onClick={handleCopy}
                            className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-700 flex items-center"
                        >
                            {copySuccess ? (
                                <>
                                    <svg className="h-3 w-3 mr-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    Copy
                                </>
                            )}
                        </button>
                    </div>
                    <pre className="p-4 text-sm overflow-x-auto">
                        <code className="text-gray-300">{template}</code>
                    </pre>
                </div>
            )}
        </div>
    );
};

// CodeFixDisplay Component
const CodeFixDisplay = ({ vulnerability, fixSuggestion, onApplyFix, onMarkFalsePositive }) => {
    const [showDiff, setShowDiff] = useState(true);
    const [applyingFix, setApplyingFix] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);

    const handleCopyCode = () => {
        navigator.clipboard.writeText(fixSuggestion.fixed_code);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };
    
    // Determine framework from file extension
    const getFramework = () => {
        const file = vulnerability.file.toLowerCase();
        if (file.endsWith('.py')) {
            // Check for Django/Flask patterns in the code
            if (vulnerability.code_snippet?.includes('request.user') || vulnerability.code_snippet?.includes('get_object_or_404')) {
                return 'python_django';
            } else if (vulnerability.code_snippet?.includes('current_user') || vulnerability.code_snippet?.includes('@app.route')) {
                return 'python_flask';
            }
        } else if (file.endsWith('.php')) {
            if (vulnerability.code_snippet?.includes('Auth::') || vulnerability.code_snippet?.includes('$this->authorize')) {
                return 'php_laravel';
            }
        }
        return null;
    };

    return (
        <div className="mt-4 border-t pt-4">
            {fixSuggestion.is_false_positive ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                        <svg className="h-5 w-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-3">
                            <h4 className="text-sm font-medium text-yellow-800">Possible False Positive</h4>
                            <p className="mt-1 text-sm text-yellow-700">{fixSuggestion.false_positive_reason}</p>
                            <button
                                onClick={() => onMarkFalsePositive(vulnerability.vulnerability_id || vulnerability.id)}
                                className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900"
                            >
                                Mark as False Positive →
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                        <svg className="h-5 w-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        AI-Generated Fix
                    </h4>
                    
                    {/* Fix explanation */}
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-900">{fixSuggestion.changes_explanation}</p>
                        {fixSuggestion.confidence && (
                            <div className="mt-2 flex items-center">
                                <span className="text-xs text-blue-700">Confidence: </span>
                                <div className="ml-2 flex-1 bg-blue-200 rounded-full h-2 max-w-[100px]">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full"
                                        style={{ width: `${fixSuggestion.confidence * 100}%` }}
                                    />
                                </div>
                                <span className="ml-2 text-xs text-blue-700">{Math.round(fixSuggestion.confidence * 100)}%</span>
                            </div>
                        )}
                    </div>

                    {/* Code diff view */}
                    <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
                            <span className="text-sm text-gray-300">Suggested Fix</span>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => setShowDiff(!showDiff)}
                                    className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-700"
                                >
                                    {showDiff ? 'Show Fixed Code Only' : 'Show Diff'}
                                </button>
                                <button
                                    onClick={handleCopyCode}
                                    className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-700 flex items-center"
                                >
                                    {copySuccess ? (
                                        <>
                                            <svg className="h-3 w-3 mr-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            Copy
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <pre className="p-4 text-sm overflow-x-auto">
                            {showDiff ? (
                                <code className="text-gray-300">
                                    <div className="mb-2">
                                        <span className="text-red-400">- </span>
                                        <span className="text-red-300">{vulnerability.code_snippet || 'Original vulnerable code'}</span>
                                    </div>
                                    <div>
                                        <span className="text-green-400">+ </span>
                                        <span className="text-green-300">{fixSuggestion.fixed_code}</span>
                                    </div>
                                </code>
                            ) : (
                                <code className="text-green-300">{fixSuggestion.fixed_code}</code>
                            )}
                        </pre>
                    </div>

                    {/* Implementation steps */}
                    {fixSuggestion.implementation_steps && fixSuggestion.implementation_steps.length > 0 && (
                        <div className="mt-4">
                            <h5 className="text-sm font-medium text-gray-900 mb-2">Implementation Steps:</h5>
                            <ol className="space-y-1">
                                {fixSuggestion.implementation_steps.map((step, idx) => (
                                    <li key={idx} className="flex items-start text-sm text-gray-600">
                                        <span className="font-medium text-gray-500 mr-2">{idx + 1}.</span>
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* Additional changes needed */}
                    {fixSuggestion.additional_changes && fixSuggestion.additional_changes.length > 0 && (
                        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <h5 className="text-sm font-medium text-amber-900 mb-1 flex items-center">
                                <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Additional Changes Required:
                            </h5>
                            <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                                {fixSuggestion.additional_changes.map((change, idx) => (
                                    <li key={idx}>{change}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Testing guidance */}
                    {fixSuggestion.testing_guidance && (
                        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                            <h5 className="text-sm font-medium text-green-900 mb-1 flex items-center">
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Testing Guidance:
                            </h5>
                            <p className="text-sm text-green-800">{fixSuggestion.testing_guidance}</p>
                        </div>
                    )}

                    {/* References */}
                    {fixSuggestion.references && fixSuggestion.references.length > 0 && (
                        <div className="mt-4">
                            <h5 className="text-sm font-medium text-gray-900 mb-1">References:</h5>
                            <ul className="space-y-1">
                                {fixSuggestion.references.map((ref, idx) => (
                                    <li key={idx}>
                                        <a 
                                            href={ref} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        >
                                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            {ref}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {/* Prevention Templates */}
                    <PreventionTemplates 
                        vulnerabilityType={vulnerability.type}
                        framework={getFramework()}
                    />
                </>
            )}
        </div>
    );
};

// Main SecurityDashboard Component
export default function SecurityDashboard() {
    // State declarations
    const [vulnerabilities, setVulnerabilities] = useState([]);
    const [selectedVuln, setSelectedVuln] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [fixSuggestion, setFixSuggestion] = useState(null);
    const [loadingFix, setLoadingFix] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        critical: 0,
        high: 0,
        medium: 0,
        low: 0
    });
    const [filters, setFilters] = useState({
        severity: 'all',
        type: 'all',
        fixed: 'unfixed',
        search: ''
    });

    
    const [scanHistory, setScanHistory] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
    });
    const [showSpecialView, setShowSpecialView] = useState(false);
    const [showView, setShowView] = useState('all');
    const [showArchived, setShowArchived] = useState(false);
    const [archivedStats, setArchivedStats] = useState({
        fixed: 0,
        false_positives: 0
    });
    const [notification, setNotification] = useState(null);
    // Effects
    useEffect(() => {
    loadData().catch(error => {
        console.error('Error loading security data:', error);
    });
}, [filters, pagination.page]);

    // Data loading functions
    const loadData = async () => {
    try {
        await fetchVulnerabilities();
    } catch (error) {
        console.error('Error fetching vulnerabilities:', error);
    }
    
    try {
        await fetchDashboard();
    } catch (error) {
        console.error('Error fetching dashboard:', error);
    }
    
    try {
        await fetchScanHistory();
    } catch (error) {
        console.error('Error fetching scan history:', error);
    }
};

    const fetchDashboard = async () => {
        try {
            const response = await getSecurityDashboard();
            setStats(response.data.summary);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        }
    };

    const fetchVulnerabilities = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit
            };
            if (filters.severity !== 'all') params.severity = filters.severity;
            if (filters.type !== 'all') params.vuln_type = filters.type;
            if (filters.fixed !== 'all') params.fixed = filters.fixed === 'fixed';

            const response = await getVulnerabilities(params);
            let filtered = response.data.vulnerabilities || [];
            
            // Apply search filter
            if (filters.search) {
                filtered = filtered.filter(v => 
                    v.file.toLowerCase().includes(filters.search.toLowerCase()) ||
                    v.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                    v.description.toLowerCase().includes(filters.search.toLowerCase())
                );
            }
            
            setVulnerabilities(filtered);
            setPagination(prev => ({
                ...prev,
                total: response.data.total || 0,
                pages: response.data.pages || 0
            }));
        } catch (error) {
            console.error('Error fetching vulnerabilities:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchScanHistory = async () => {
    try {
        const response = await getScanHistory();
        // Check if response has data and scans array
        if (response && response.data && response.data.scans) {
            setScanHistory(response.data.scans);
        } else {
            setScanHistory([]);
        }
    } catch (error) {
        console.error('Error fetching scan history:', error);
        // Don't let the error crash the component
        setScanHistory([]);
        // Optionally show a toast notification
        // toast.warning('Could not load scan history');
    }
};

    // Action handlers
    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleStartScan = async () => {
        setScanning(true);
        try {
            await startSecurityScan({ scan_type: 'full' });
            
            // Poll for completion
            const pollInterval = setInterval(async () => {
                const historyResponse = await getScanHistory({ limit: 1 });
                const latestScan = historyResponse.data.scans[0];
                
                if (latestScan?.status === 'completed') {
                    clearInterval(pollInterval);
                    setScanning(false);
                    await loadData();
                }
            }, 5000);
            
        } catch (error) {
            console.error('Error starting scan:', error);
            setScanning(false);
        }
    };

    const fetchAIFix = async (vuln) => {
        setLoadingFix(true);
        try {
            const response = await getAIFixSuggestion(vuln.vulnerability_id || vuln.id);
            setFixSuggestion(response.data);
        } catch (error) {
            // If 404, fallback to basic recommendation, else log error
            if (error.response && error.response.status === 404) {
                setFixSuggestion({
                    fixed_code: vuln.recommendation,
                    changes_explanation: vuln.recommendation,
                    implementation_steps: ['Review the vulnerable code', 'Apply the recommended fix', 'Test thoroughly'],
                    references: []
                });
            } else {
                console.error('Error fetching AI fix:', error);
                setFixSuggestion({
                    fixed_code: vuln.recommendation,
                    changes_explanation: vuln.recommendation,
                    implementation_steps: ['Review the vulnerable code', 'Apply the recommended fix', 'Test thoroughly'],
                    references: []
                });
            }
        } finally {
            setLoadingFix(false);
        }
    };

    const showNotification = (message, type = 'info') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleMarkFixed = async (vulnId) => {
        try {
            await markVulnerabilityFixed(vulnId);
            await loadData();
            setSelectedVuln(null);
            setFixSuggestion(null);
            // Show success notification
            showNotification('Vulnerability marked as fixed', 'success');
        } catch (error) {
            console.error('Error marking fixed:', error);
            showNotification('Failed to mark vulnerability as fixed', 'error');
        }
    };

    const handleMarkFalsePositive = async (vulnId) => {
        try {
            await markFalsePositive(vulnId);
            await loadData();
            setSelectedVuln(null);
            setFixSuggestion(null);
            showNotification('Marked as false positive', 'success');
        } catch (error) {
            console.error('Error marking false positive:', error);
            showNotification('Failed to mark as false positive', 'error');
        }
    };

    const handleApplyFix = async (vulnId) => {
        // For now, just copy the fix code
        if (fixSuggestion && fixSuggestion.fixed_code) {
            navigator.clipboard.writeText(fixSuggestion.fixed_code);
            // You could show a toast notification here
            console.log('Fix code copied to clipboard');
        }
    };

    const handleExport = async (format) => {
        try {
            const response = await exportVulnerabilities({ 
                format, 
                severity: filters.severity !== 'all' ? filters.severity : undefined 
            });
            
            // Handle download based on format
            const blob = new Blob([
                format === 'json' ? JSON.stringify(response.data, null, 2) : response.data
            ], { 
                type: format === 'json' ? 'application/json' : 
                    format === 'csv' ? 'text/csv' : 
                    'text/markdown' 
            });
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vulnerabilities.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error exporting vulnerabilities:', error);
        }
    };

    const handleSelectVulnerability = (vuln) => {
        setSelectedVuln(vuln);
        setFixSuggestion(null);
        setShowSpecialView(false);
    };

    // Utility functions
    const getSeverityColor = (severity) => {
        switch (severity.toUpperCase()) {
            case 'CRITICAL': return 'text-red-600 bg-red-50 border-red-200';
            case 'HIGH': return 'text-orange-600 bg-orange-50 border-orange-200';
            case 'MEDIUM': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'LOW': return 'text-blue-600 bg-blue-50 border-blue-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getSeverityIcon = (severity) => {
        const icons = {
            'CRITICAL': '🔴',
            'HIGH': '🟠',
            'MEDIUM': '🟡',
            'LOW': '🔵'
        };
        return icons[severity.toUpperCase()] || '⚪';
    };

    const getTypeIcon = (type) => {
        const icons = {
            'SQLI': '💉',
            'XSS': '🔗',
            'IDOR': '🔓',
            'CMD_INJECTION': '💻',
            'PATH_TRAVERSAL': '📁',
            'HARDCODED_CRED': '🔑',
            'FILE_UPLOAD': '📤',
            'WEAK_CRYPTO': '🔐',
            'AUTH_MISSING': '🚫',
            'INFO_DISCLOSURE': '📢',
            'RACE_CONDITION': '🏃',
            'IDOR_FILE_ACCESS': '📂',
            'IDOR_DELETE_OPERATION': '🗑️',
            'IDOR_USER_DATA_ACCESS': '👤',
            'RACE_CONDITION_TOCTOU': '⏱️'
        };
        return icons[type] || '⚠️';
    };

    // Pagination controls component
    const PaginationControls = () => (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t">
            <div className="flex items-center">
                <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                    <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span> of{' '}
                    <span className="font-medium">{pagination.total}</span> results
                </p>
            </div>
            <div className="flex items-center space-x-2">
                <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                
                {/* Page numbers */}
                <div className="flex space-x-1">
                    {[...Array(Math.min(5, pagination.pages))].map((_, idx) => {
                        let pageNum;
                        if (pagination.pages <= 5) {
                            pageNum = idx + 1;
                        } else if (pagination.page <= 3) {
                            pageNum = idx + 1;
                        } else if (pagination.page >= pagination.pages - 2) {
                            pageNum = pagination.pages - 4 + idx;
                        } else {
                            pageNum = pagination.page - 2 + idx;
                        }
                        
                        return (
                            <button
                                key={idx}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1 text-sm font-medium border rounded-md ${
                                    pageNum === pagination.page
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-700 bg-white hover:bg-gray-50'
                                }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                </div>
                
                <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header with Action Buttons */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 flex items-center">
                            <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            Security Vulnerability Scanner
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Scan your codebase for security vulnerabilities and get AI-powered fix suggestions
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <button
                                onClick={() => {
                                    setShowSpecialView(!showSpecialView);
                                    setShowView(showSpecialView ? 'all' : 'idor');
                                }}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            {showSpecialView ? 'Show All' : 'IDOR & Race'}
                        </button>
                        <button
                            onClick={() => {
                                setShowView('aws');
                                setShowSpecialView(false);
                            }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                            </svg>
                            AWS Credentials
                        </button>
                        <div className="relative">
                            <button
                                onClick={() => document.getElementById('export-menu').classList.toggle('hidden')}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                </svg>
                                Export
                            </button>
                            <div id="export-menu" className="hidden absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                <div className="py-1">
                                    <button onClick={() => handleExport('json')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Export as JSON</button>
                                    <button onClick={() => handleExport('csv')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Export as CSV</button>
                                    <button onClick={() => handleExport('markdown')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Export as Markdown</button>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleStartScan}
                            disabled={scanning}
                            className={`inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-white ${
                                scanning 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {scanning ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Scanning...
                                </>
                            ) : (
                                <>
                                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Run Security Scan
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Last Scan Info */}
                {scanHistory.length > 0 && scanHistory[0].status === 'completed' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center">
                            <svg className="h-4 w-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-blue-900">
                                Last scan: {new Date(scanHistory[0].completed_at || scanHistory[0].started_at).toLocaleString()}
                                {' • '}{scanHistory[0].files_scanned} files scanned
                                {' • '}{scanHistory[0].vulnerabilities_found} issues found
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Issues</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                        </div>
                        <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-red-600">Critical</p>
                            <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
                        </div>
                        <span className="text-2xl">{getSeverityIcon('CRITICAL')}</span>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-orange-600">High</p>
                            <p className="text-2xl font-bold text-orange-600">{stats.high}</p>
                        </div>
                        <span className="text-2xl">{getSeverityIcon('HIGH')}</span>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-yellow-600">Medium</p>
                            <p className="text-2xl font-bold text-yellow-600">{stats.medium}</p>
                        </div>
                        <span className="text-2xl">{getSeverityIcon('MEDIUM')}</span>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-blue-600">Low</p>
                            <p className="text-2xl font-bold text-blue-600">{stats.low}</p>
                        </div>
                        <span className="text-2xl">{getSeverityIcon('LOW')}</span>
                    </div>
                </div>
            </div>

            {/* Special IDOR & Race Condition View */}
            {showSpecialView && (
                <IDORAndRaceView 
                    vulnerabilities={vulnerabilities} 
                    onSelectVulnerability={handleSelectVulnerability}
                />
            )}

            {/* Filters */}
            {!showSpecialView && (
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search vulnerabilities..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        
                        <select
                            value={filters.severity}
                            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Severities</option>
                            <option value="CRITICAL">Critical</option>
                            <option value="HIGH">High</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="LOW">Low</option>
                        </select>
                        
                        <select
                            value={filters.type}
                            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Types</option>
                            <option value="SQLI">SQL Injection</option>
                            <option value="XSS">Cross-Site Scripting</option>
                            <option value="IDOR">IDOR</option>
                            <option value="CMD_INJECTION">Command Injection</option>
                            <option value="PATH_TRAVERSAL">Path Traversal</option>
                            <option value="HARDCODED_CRED">Hardcoded Credentials</option>
                            <option value="FILE_UPLOAD">File Upload</option>
                            <option value="WEAK_CRYPTO">Weak Cryptography</option>
                            <option value="RACE_CONDITION">Race Condition</option>
                        </select>
                        
                        <select
                            value={filters.fixed}
                            onChange={(e) => setFilters({ ...filters, fixed: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Status</option>
                            <option value="unfixed">Unfixed</option>
                            <option value="fixed">Fixed</option>
                        </select>
                        
                        <button
                            onClick={() => setFilters({ severity: 'all', type: 'all', fixed: 'unfixed', search: '' })}
                            className="flex items-center justify-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Clear</span>
                        </button>
                    </div>
                </div>
            )}

            {/* AWS Credential View */}
            {showView === 'aws' && (
                <AWSCredentialView 
                    vulnerabilities={vulnerabilities} 
                    onSelectVulnerability={handleSelectVulnerability}
                />
            )}

            {/* Main Content */}
            {!showSpecialView && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Vulnerability List */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow">
                            <div className="p-4 border-b">
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Vulnerabilities ({pagination.total})
                                </h2>
                            </div>
                            
                            {loading ? (
                                <div className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="text-gray-500 mt-2">Loading vulnerabilities...</p>
                                </div>
                            ) : vulnerabilities.length === 0 ? (
                                <div className="p-8 text-center">
                                    <svg className="h-12 w-12 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-gray-500">No vulnerabilities found!</p>
                                    <p className="text-sm text-gray-400 mt-1">Your code is looking secure 🎉</p>
                                </div>
                            ) : (
                                <>
                                    <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                                        {vulnerabilities.map((vuln) => (
                                            <div
                                                key={vuln.id}
                                                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                                    selectedVuln?.id === vuln.id ? 'bg-blue-50' : ''
                                                }`}
                                                onClick={() => handleSelectVulnerability(vuln)}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <span className="text-lg">{getTypeIcon(vuln.type)}</span>
                                                            <h3 className="font-medium text-gray-900">{vuln.title}</h3>
                                                            {vuln.fixed && (
                                                                <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                                                    <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                    Fixed
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        <p className="text-sm text-gray-600 mb-2">{vuln.description}</p>
                                                        
                                                        <div className="flex items-center space-x-4 text-sm">
                                                            <div className="flex items-center space-x-1 text-gray-500">
                                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                                <span className="truncate max-w-xs">{vuln.file.split('/').slice(-2).join('/')}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1 text-gray-500">
                                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                                                </svg>
                                                                <span>Line {vuln.line}</span>
                                                            </div>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(vuln.severity)}`}>
                                                                {vuln.severity}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    <svg className="h-5 w-5 text-gray-400 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <PaginationControls />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Vulnerability Details */}
                    <div className="lg:col-span-1">
                        {selectedVuln ? (
                            <div className="bg-white rounded-lg shadow sticky top-6">
                                <div className="p-4 border-b">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-lg font-semibold text-gray-900">Vulnerability Details</h3>
                                        <button
                                            onClick={() => {
                                                setSelectedVuln(null);
                                                setFixSuggestion(null);
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                        >
                                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-2xl">{getTypeIcon(selectedVuln.type)}</span>
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getSeverityColor(selectedVuln.severity)}`}>
                                            {selectedVuln.severity}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Location</h4>
                                        <p className="text-sm text-gray-600 font-mono">{selectedVuln.file}</p>
                                        <p className="text-sm text-gray-600">Line {selectedVuln.line}</p>
                                    </div>
                                    
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Description</h4>
                                        <p className="text-sm text-gray-600">{selectedVuln.description}</p>
                                    </div>
                                    
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Impact</h4>
                                        <p className="text-sm text-gray-600">{selectedVuln.impact}</p>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-1">CWE ID</h4>
                                            <p className="text-sm text-gray-600">{selectedVuln.cwe_id}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-1">Difficulty</h4>
                                            <p className="text-sm text-gray-600">{selectedVuln.exploitation_difficulty}</p>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">OWASP Category</h4>
                                        <p className="text-sm text-gray-600">{selectedVuln.owasp_category}</p>
                                    </div>
                                    
                                    {/* Basic Recommendation (always visible) */}
                                    <div className="border-t pt-4">
                                        <h4 className="font-medium text-gray-900 mb-2">Basic Recommendation</h4>
                                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                                            <p className="text-gray-700">{selectedVuln.recommendation}</p>
                                        </div>
                                    </div>
                                    
                                    {/* AI Fix Button or Fix Display */}
                                    {!fixSuggestion ? (
                                        <button
                                            onClick={() => fetchAIFix(selectedVuln)}
                                            disabled={loadingFix}
                                            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                                        >
                                            {loadingFix ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span>Generating AI Fix...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                    </svg>
                                                    <span>Get AI-Powered Fix</span>
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <CodeFixDisplay
                                            vulnerability={selectedVuln}
                                            fixSuggestion={fixSuggestion}
                                            onApplyFix={handleApplyFix}
                                            onMarkFalsePositive={handleMarkFalsePositive}
                                        />
                                    )}
                                    
                                    {/* Action buttons */}
                                    <div className="flex space-x-2 pt-4 border-t">
                                        {!selectedVuln.fixed ? (
                                            <>
                                                <button
                                                    onClick={() => handleMarkFixed(selectedVuln.vulnerability_id || selectedVuln.id)}
                                                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm transition-colors"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span>Mark Fixed</span>
                                                </button>
                                                <button
                                                    onClick={() => handleMarkFalsePositive(selectedVuln.vulnerability_id || selectedVuln.id)}
                                                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm transition-colors"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    <span>False Positive</span>
                                                </button>
                                            </>
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center text-green-600 py-2">
                                                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <span className="font-medium">Marked as Fixed</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                                <svg className="h-12 w-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <p>Select a vulnerability to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const AWSCredentialView = ({ vulnerabilities, onSelectVulnerability }) => {
    const awsVulns = vulnerabilities.filter(v => v.type.includes('AWS_CREDENTIAL'));
    
    // Group by credential type
    const groupedVulns = {
        access_keys: awsVulns.filter(v => v.type.includes('ACCESS_KEY')),
        secret_keys: awsVulns.filter(v => v.type.includes('SECRET_ACCESS_KEY')),
        urls: awsVulns.filter(v => v.type.includes('S3_URLS')),
        console: awsVulns.filter(v => v.type.includes('CONSOLE')),
        other: awsVulns.filter(v => !v.type.includes('ACCESS_KEY') && !v.type.includes('SECRET_ACCESS_KEY') && !v.type.includes('S3_URLS') && !v.type.includes('CONSOLE'))
    };
    
    const getIconForType = (type) => {
        if (type.includes('ACCESS_KEY')) return '🔑';
        if (type.includes('SECRET')) return '🔐';
        if (type.includes('CONSOLE')) return '📺';
        if (type.includes('URL')) return '🔗';
        return '☁️';
    };
    
    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                    <span className="text-2xl mr-2">☁️</span>
                    AWS Credential Exposure ({awsVulns.length})
                </h3>
                
                {awsVulns.length > 0 && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                        <strong className="font-bold">⚠️ Critical Security Alert!</strong>
                        <p className="mt-1">AWS credentials detected in your codebase. Rotate these credentials immediately!</p>
                    </div>
                )}
            </div>
            
            {awsVulns.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <svg className="h-12 w-12 mx-auto mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>No AWS credentials found in codebase!</p>
                    <p className="text-sm text-gray-400 mt-1">Your AWS credentials are properly secured 🎉</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Access Keys Section */}
                    {groupedVulns.access_keys.length > 0 && (
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <span className="text-xl mr-2">🔑</span>
                                Access Key IDs ({groupedVulns.access_keys.length})
                            </h4>
                            <div className="space-y-2">
                                {groupedVulns.access_keys.map((vuln) => (
                                    <VulnerabilityCard key={vuln.id} vuln={vuln} onClick={() => onSelectVulnerability(vuln)} />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Secret Keys Section */}
                    {groupedVulns.secret_keys.length > 0 && (
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <span className="text-xl mr-2">🔐</span>
                                Secret Access Keys ({groupedVulns.secret_keys.length})
                            </h4>
                            <div className="space-y-2">
                                {groupedVulns.secret_keys.map((vuln) => (
                                    <VulnerabilityCard key={vuln.id} vuln={vuln} onClick={() => onSelectVulnerability(vuln)} />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Console Exposure Section */}
                    {groupedVulns.console.length > 0 && (
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <span className="text-xl mr-2">📺</span>
                                Console/Log Exposure ({groupedVulns.console.length})
                            </h4>
                            <div className="space-y-2">
                                {groupedVulns.console.map((vuln) => (
                                    <VulnerabilityCard key={vuln.id} vuln={vuln} onClick={() => onSelectVulnerability(vuln)} />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* URL Exposure Section */}
                    {groupedVulns.urls.length > 0 && (
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <span className="text-xl mr-2">🔗</span>
                                Credentials in URLs ({groupedVulns.urls.length})
                            </h4>
                            <div className="space-y-2">
                                {groupedVulns.urls.map((vuln) => (
                                    <VulnerabilityCard key={vuln.id} vuln={vuln} onClick={() => onSelectVulnerability(vuln)} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            {/* Quick Actions */}
            {awsVulns.length > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">🚨 Immediate Actions Required:</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                        <li>Rotate all exposed AWS credentials immediately</li>
                        <li>Check AWS CloudTrail for any unauthorized access</li>
                        <li>Remove credentials from source code</li>
                        <li>Set up AWS IAM roles or use environment variables</li>
                        <li>Add .env files to .gitignore</li>
                        <li>Run git-secrets to prevent future commits</li>
                    </ol>
                </div>
            )}
        </div>
    );
};

// Vulnerability Card Component
const VulnerabilityCard = ({ vuln, onClick }) => {
    const getMatchedCredential = () => {
        if (vuln.matched_pattern && vuln.matched_pattern.length > 20) {
            // Mask the credential for security
            const pattern = vuln.matched_pattern;
            if (pattern.startsWith('AKIA') || pattern.startsWith('ASIA')) {
                return pattern.substring(0, 8) + '****' + pattern.substring(pattern.length - 4);
            }
            return pattern.substring(0, 6) + '****';
        }
        return vuln.matched_pattern;
    };
    
    return (
        <div 
            className="p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
            onClick={onClick}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <p className="font-medium text-red-900">{vuln.title}</p>
                    <p className="text-sm text-red-700 mt-1 font-mono">{vuln.file}:{vuln.line}</p>
                    {vuln.matched_pattern && (
                        <p className="text-xs text-red-600 mt-1 font-mono bg-red-100 px-2 py-1 rounded inline-block">
                            Found: {getMatchedCredential()}
                        </p>
                    )}
                </div>
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                    CRITICAL
                </span>
            </div>
        </div>
    );
};

// Add AWS prevention templates
const AWS_PREVENTION_TEMPLATES = {
    'env_variables': `# Use environment variables (Python)
import os
aws_access_key_id = os.environ.get('AWS_ACCESS_KEY_ID')
aws_secret_access_key = os.environ.get('AWS_SECRET_ACCESS_KEY')

# Never hardcode credentials
# BAD: aws_access_key_id = 'AKIAIOSFODNN7EXAMPLE'`,
    
    'iam_roles': `# Use IAM roles for EC2/Lambda (Python boto3)
import boto3

# Let boto3 use the IAM role automatically
s3 = boto3.client('s3')  # No credentials needed!

# For Lambda functions, attach an IAM role with necessary permissions`,
    
    'secrets_manager': `# Use AWS Secrets Manager
import boto3
import json

def get_secret():
    secret_name = "MyAppSecrets"
    region_name = "us-east-1"
    
    session = boto3.session.Session()
    client = session.client(
        service_name='secretsmanager',
        region_name=region_name
    )
    
    get_secret_value_response = client.get_secret_value(
        SecretId=secret_name
    )
    
    secret = get_secret_value_response['SecretString']
    return json.loads(secret)`,
    
    'credentials_file': `# Use AWS credentials file
# ~/.aws/credentials
[default]
aws_access_key_id = YOUR_ACCESS_KEY
aws_secret_access_key = YOUR_SECRET_KEY

# ~/.aws/config
[default]
region = us-east-1

# Then in code:
import boto3
s3 = boto3.client('s3')  # Uses credentials file automatically`,

    'git_secrets': `# Install git-secrets to prevent committing credentials
# https://github.com/awslabs/git-secrets

# Install
brew install git-secrets  # macOS
# or
git clone https://github.com/awslabs/git-secrets.git

# Setup for your repo
cd /path/to/repo
git secrets --install
git secrets --register-aws

# This will prevent commits containing AWS credentials`
};

